import React, { useState, useEffect } from 'react';
import { ChartDisplay } from './charts';
import ColumnHeaderDropdown from './ColumnHeaderDropdown';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
} from '../../utils/formatUtils';
import {
  buildExchangeRateLookupFromCurrencyList,
  calculateReportAggregation,
  formatAggregatedDisplayForReport,
  formatReportCurrencyCell,
} from '../../utils/reportCurrencyAggregate';
import CurrencyApi from '../../api/CurrencyApi';
import jwt_decode from 'jwt-decode';
import { getMultiCurrencySettings } from '../../utils/multiCurrencySettings';

// Resolve currency with priority: token currency (user currency or company currency) > default
const resolveCurrency = (fallbackCurrency = 'INR') => {
  // Check currency from JWT token
  // Token currency is COALESCE(u.currency, c.currency) from auth.model.js
  // So it will be user.currency if available, otherwise company.currency
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = jwt_decode(token);
      // Currency in token is: user.currency if available, otherwise company.currency
      const tokenCurrency = decoded.currency;
      if (tokenCurrency) {
        return tokenCurrency;
      }
    }
  } catch (error) {
    console.error('Error decoding token for currency:', error);
  }

  // Fallback to default
  return fallbackCurrency;
};

const parseDisplayConfig = (displayConfig) => {
  if (!displayConfig) return {};
  if (typeof displayConfig === 'string') {
    try {
      return JSON.parse(displayConfig);
    } catch (e) {
      return {};
    }
  }
  return displayConfig || {};
};

// Same rules as ReportDataViewer / DetailLayout: universal date & datetime display + user timezone
const formatValue = (fieldData, dataType, displayConfig = null) => {
  // Handle enriched data structure (from backend with display values)
  if (typeof fieldData === 'object' && fieldData !== null) {
    // If it's a lookup field with display value, show the display value
    if (fieldData.isReference && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    // If it's a picklist field with display value, show the display value
    if ((fieldData.isPicklist || fieldData.isMultiPicklist) && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    // Otherwise, use the value property
    const value = fieldData.value;
    if (value === null || value === undefined || value === '') return 'N/A';

    if (dataType === 'rollupsummary' && displayConfig) {
      const parsedConfig = parseDisplayConfig(displayConfig);
      const rollupConfig = parsedConfig.rollupSummary || {};
      const outputType = (rollupConfig.outputType || 'number').toLowerCase();

      if (outputType === 'currency') {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 'N/A';
        const currencyCode = resolveCurrency('INR');
        return formatCurrency(numValue, currencyCode);
      }
      if (outputType === 'date') {
        return formatDate(value);
      }
      if (outputType === 'datetime') {
        return formatDateTime(value);
      }
      const numVal = parseFloat(value);
      if (isNaN(numVal)) return 'N/A';
      return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    switch (dataType) {
      case 'currency': {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 'N/A';
        const currencyCode = resolveCurrency('INR');
        return formatCurrency(numValue, currencyCode, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
      case 'number':
      case 'decimal': {
        const numVal = parseFloat(value);
        if (isNaN(numVal)) return 'N/A';
        return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      case 'percent': {
        const percentVal = parseFloat(value);
        if (isNaN(percentVal)) return 'N/A';
        return formatPercent(percentVal);
      }
      case 'date':
        return formatDate(value);
      case 'datetime':
        return formatDateTime(value);
      default:
        return value.toString();
    }
  }

  // Handle simple value (fallback for backward compatibility)
  const value = fieldData;
  if (value === null || value === undefined || value === '') return 'N/A';

  if (dataType === 'rollupsummary' && displayConfig) {
    const parsedConfig = parseDisplayConfig(displayConfig);
    const rollupConfig = parsedConfig.rollupSummary || {};
    const outputType = (rollupConfig.outputType || 'number').toLowerCase();

    if (outputType === 'currency') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return 'N/A';
      return formatCurrency(numValue, resolveCurrency('INR'));
    }
    if (outputType === 'date') {
      return formatDate(value);
    }
    if (outputType === 'datetime') {
      return formatDateTime(value);
    }
    const numVal = parseFloat(value);
    if (isNaN(numVal)) return 'N/A';
    return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  switch (dataType) {
    case 'currency': {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return 'N/A';
      return formatCurrency(numValue, resolveCurrency('INR'));
    }
    case 'number':
    case 'decimal':
    case 'rollupsummary': {
      const numVal = parseFloat(value);
      if (isNaN(numVal)) return 'N/A';
      return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    case 'percent': {
      const percentVal = parseFloat(value);
      if (isNaN(percentVal)) return 'N/A';
      return formatPercent(percentVal);
    }
    case 'date':
      return formatDate(value);
    case 'datetime':
      return formatDateTime(value);
    default:
      return value.toString();
  }
};

function makePreviewAggregationFormat(baseCurrencyCode) {
  return (result, field, aggregation) =>
    formatAggregatedDisplayForReport(
      result,
      field,
      aggregation,
      baseCurrencyCode,
      (res, fld) =>
        formatValue(res, fld.data_type, fld.display_config || fld.displayConfig)
    );
}

// Function to render grouped data
const renderGroupedData = (
  data,
  selectedFields,
  grouping,
  summarizedFields,
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  reportSortKeys = []
) => {
  const aggregationFormatter = makePreviewAggregationFormat(baseCurrencyCode);

  const formatDataCell = (fieldData, field, row) => {
    const money = formatReportCurrencyCell(
      fieldData,
      field,
      row,
      baseCurrencyCode,
      resolveCurrency('INR'),
      exchangeRateLookup
    );
    if (money != null) return money;
    return formatValue(
      fieldData,
      field.data_type,
      field.display_config || field.displayConfig
    );
  };

  // Get all grouped fields
  const groupedFields = grouping.groupedFields;

  console.log('renderGroupedData called with:', {
    dataLength: data.length,
    selectedFieldsLength: selectedFields.length,
    grouping,
    summarizedFields,
    sampleData: data.length > 0 ? data[0] : 'No data',
    groupedFields: groupedFields.map(f => ({ name: f.field_name, label: f.field_label, object: f.object_name }))
  });
  console.log('Grouped fields:', groupedFields);

  // Filter out grouped fields from selectedFields to avoid duplication
  const nonGroupedFields = selectedFields.filter(field =>
    !grouping.groupedFields.some(groupField =>
      groupField.field_name === field.field_name &&
      groupField.object_name === field.object_name
    )
  );

  console.log('Non grouped fields:', nonGroupedFields);

  // Helper function to get field value from row
  const getFieldValue = (field, row) => {
    const tableName = field.object_name || 'main';
    const aliasedFieldName = `${tableName}_${field.field_name}`;
    const fieldData = row[aliasedFieldName] || row[field.field_name];

    console.log(`Getting field value for ${field.field_name}:`, {
      tableName,
      aliasedFieldName,
      fieldData,
      fieldDataType: typeof fieldData,
      availableKeys: Object.keys(row)
    });

    // Handle null/undefined values
    if (fieldData === null || fieldData === undefined) {
      return 'N/A';
    }

    const dt = (field.data_type || '').toLowerCase();

    // Handle object values (wrapped data from backend)
    if (typeof fieldData === 'object' && fieldData !== null) {
      // If it's a wrapped object with value and displayValue properties
      if (fieldData.hasOwnProperty('value') && fieldData.hasOwnProperty('displayValue')) {
        // For checkbox/boolean fields, use the displayValue if available, otherwise convert the value
        if (typeof fieldData.value === 'boolean') {
          return fieldData.displayValue || (fieldData.value ? 'true' : 'false');
        }
        if (dt === 'date' && fieldData.value != null && fieldData.value !== '') {
          return formatDate(fieldData.value);
        }
        if (dt === 'datetime' && fieldData.value != null && fieldData.value !== '') {
          return formatDateTime(fieldData.value);
        }
        // For other fields, use displayValue if available, otherwise use value
        return fieldData.displayValue || String(fieldData.value) || 'N/A';
      }
      // If it's a regular object, try to extract displayValue or value
      const inner = fieldData.displayValue || fieldData.value;
      if (dt === 'date' && inner != null && inner !== '') {
        return formatDate(inner);
      }
      if (dt === 'datetime' && inner != null && inner !== '') {
        return formatDateTime(inner);
      }
      return String(inner || 'N/A');
    }

    // Handle boolean values (including false) - raw boolean values
    if (typeof fieldData === 'boolean') {
      return fieldData ? 'true' : 'false';
    }

    if (dt === 'date' && fieldData != null && fieldData !== '') {
      return formatDate(fieldData);
    }
    if (dt === 'datetime' && fieldData != null && fieldData !== '') {
      return formatDateTime(fieldData);
    }

    // Handle all other types (string, number, etc.)
    return String(fieldData);
  };

  // Simple groupByFields function
  function groupByFields(data, groupFields) {
    console.log('groupByFields called with:', {
      dataLength: data.length,
      groupFields: groupFields.map(f => ({ name: f.field_name, label: f.field_label, object: f.object_name })),
      sampleRow: data.length > 0 ? data[0] : 'No data'
    });

    //let currentLevel = [];

    return data.reduce((acc, row) => {
      let currentLevel = acc;
      console.log('currentLevel: ', currentLevel);
      console.log("row: ", row);
      groupFields.forEach((field, index) => {
        const fieldValue = getFieldValue(field, row);
        console.log(`${index} -->Processing field ${field.field_name}, got value:`, fieldValue);

        // if value not initialized, create new object/array
        if (!currentLevel[fieldValue]) {
          currentLevel[fieldValue] = (index === groupFields.length - 1) ? [] : {};
        }

        // go deeper
        if (index === groupFields.length - 1) {
          currentLevel[fieldValue].push(row);
        } else {
          currentLevel = currentLevel[fieldValue];
        }
      });

      return acc;
    }, {});
  }

  const nestedGroups = groupByFields(data, groupedFields);

  // When SQL sort includes a grouped column, the server only orders by group keys.
  // Apply additional sort keys (non-grouped columns) inside each leaf bucket only.
  const groupIds = new Set(
    (grouping.groupedFields || []).map(g => g.id).filter(Boolean)
  );
  const sortIncludesGroupedColumn =
    Array.isArray(reportSortKeys) &&
    reportSortKeys.some(k => k.field_config_id && groupIds.has(k.field_config_id));
  const withinGroupSortKeys = (reportSortKeys || []).filter(
    k => k.field_config_id && !groupIds.has(k.field_config_id)
  );

  const resolveFieldForSortKey = key => {
    if (!key?.field_config_id) return null;
    return (
      selectedFields.find(
        f =>
          f.id === key.field_config_id ||
          f.field_config_id === key.field_config_id
      ) ||
      (grouping.groupedFields || []).find(
        g =>
          g.id === key.field_config_id ||
          g.field_config_id === key.field_config_id
      )
    );
  };

  const getComparableForSort = (field, row) => {
    if (!field) return null;
    const tableName = field.object_name || 'main';
    const aliased = `${tableName}_${field.field_name}`;
    const raw = row[aliased] || row[field.field_name];
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'object' && raw !== null && 'value' in raw) {
      const v = raw.value;
      if (v === null || v === undefined) return null;
      if (typeof v === 'number') return v;
      if (typeof v === 'boolean') return v ? 1 : 0;
      const n = parseFloat(v);
      if (
        !Number.isNaN(n) &&
        String(v).trim() !== '' &&
        Number.isFinite(n)
      ) {
        return n;
      }
      return String(v).toLowerCase();
    }
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'boolean') return raw ? 1 : 0;
    const n = parseFloat(raw);
    if (!Number.isNaN(n) && String(raw).trim() !== '') return n;
    return String(raw).toLowerCase();
  };

  const compareSortValues = (a, b) => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  function sortLeafRowsInPlace(node, depth) {
    if (!sortIncludesGroupedColumn || withinGroupSortKeys.length === 0) return;
    if (depth === groupedFields.length - 1) {
      if (!Array.isArray(node)) return;
      node.sort((rowA, rowB) => {
        for (const key of withinGroupSortKeys) {
          const field = resolveFieldForSortKey(key);
          const va = getComparableForSort(field, rowA);
          const vb = getComparableForSort(field, rowB);
          const c = compareSortValues(va, vb);
          if (c !== 0) {
            return key.direction === 'DESC' ? -c : c;
          }
        }
        return 0;
      });
      return;
    }
    if (node && typeof node === 'object' && !Array.isArray(node)) {
      Object.keys(node).forEach(childKey => {
        sortLeafRowsInPlace(node[childKey], depth + 1);
      });
    }
  }

  sortLeafRowsInPlace(nestedGroups, 0);

  console.log('Nested groups:', nestedGroups);

  // Create printedGroups and calculatedGroup objects
  const printedGroups = {};
  const calculatedGroup = {};

  // First, initialize all group keys from nestedGroups with hierarchical composite keys
  const initializeGroups = (groups, fieldIndex = 0, parentPath = '') => {
    for (const key in groups) {
      const value = groups[key];
      const field = groupedFields[fieldIndex];
      const currentPath = parentPath ? `${parentPath}|${key}` : key;
      const compositeKey = `${field.field_name}_${currentPath}`;

      if (Array.isArray(value)) {
        // This is the final level - just add the composite key
        printedGroups[compositeKey] = false;
        calculatedGroup[compositeKey] = 0; // Initialize to 0
      } else if (typeof value === 'object' && value !== null) {
        // This is a nested object - add the composite key and recurse
        printedGroups[compositeKey] = false;
        calculatedGroup[compositeKey] = 0; // Initialize to 0
        initializeGroups(value, fieldIndex + 1, currentPath);
      }
    }
  };

  initializeGroups(nestedGroups);

  // Helper function to find the hierarchical path for a row in nestedGroups
  const findRowPath = (row, groups, fieldIndex = 0, currentPath = '') => {
    const field = groupedFields[fieldIndex];
    const fieldValue = getFieldValue(field, row);

    if (fieldValue === 'N/A' || !groups[fieldValue]) {
      return null;
    }

    const newPath = currentPath ? `${currentPath}|${fieldValue}` : fieldValue;

    if (fieldIndex === groupedFields.length - 1) {
      // This is the last field, check if this row exists in the array
      const rows = groups[fieldValue];
      if (Array.isArray(rows) && rows.includes(row)) {
        return newPath;
      }
    } else {
      // Continue to next level
      return findRowPath(row, groups[fieldValue], fieldIndex + 1, newPath);
    }

    return null;
  };

  // Now iterate over all data to fill the counts using hierarchical composite keys
  data.forEach((row, rowIndex) => {
    const rowPath = findRowPath(row, nestedGroups);
    if (rowPath && typeof rowPath === 'string') {
      groupedFields.forEach((field, fieldIndex) => {
        const fieldValue = getFieldValue(field, row);
        if (fieldValue) {
          // Create path up to current field level
          const pathParts = rowPath.split('|');
          const currentPath = pathParts.slice(0, fieldIndex + 1).join('|');
          const compositeKey = `${field.field_name}_${currentPath}`;

          if (calculatedGroup.hasOwnProperty(compositeKey)) {
            calculatedGroup[compositeKey]++;
            console.log(`Row ${rowIndex}: Field ${field.field_name} = "${fieldValue}", path: "${currentPath}", composite key: "${compositeKey}", count now: ${calculatedGroup[compositeKey]}`);
          }
        }
      });
    }
  });

  console.log('Printed groups initialized:', printedGroups);
  console.log('Calculated groups initialized:', calculatedGroup);

  // Calculate grand total
  const grandTotal = data.length;

  // If no groups were created, show error message
  if (!nestedGroups || Object.keys(nestedGroups).length === 0) {
    console.error('No groups created. Data:', data, 'Grouped fields:', groupedFields);
    return (
      <div>
        <div style={{ color: 'red', padding: '10px' }}>
          Error: No groups could be created. Check console for details.
        </div>
        <div style={{ padding: '10px' }}>
          <strong>Debug Info:</strong>
          <br />Data length: {data.length}
          <br />Grouped fields: {groupedFields.map(f => f.field_name).join(', ')}
          <br />Sample data: {JSON.stringify(data[0] || {}, null, 2)}
        </div>
      </div>
    );
  }

  // Track subtotals for each group
  const groupSubtotals = {};

  // Render nested grouped data
  const renderNestedGroups = (groups, level = 0, parentKey = '') => {
    if (!groups || typeof groups !== 'object') {
      console.error('Invalid groups in renderNestedGroups:', groups, 'level:', level);
      return <div>Error: Invalid groups data</div>;
    }

    const isLastLevel = level === groupedFields.length - 1;
    console.log("isLastLevel: ", isLastLevel, "level: ", level, "parentKey: ", parentKey);

    const results = [];

    for (const key in groups) {
      const value = groups[key];
      console.log("key: ", key, "value: ", value);

      if (Array.isArray(value)) {
        // Last level → render rows
        const groupKey = parentKey ? `${parentKey}|${key}` : key;

        results.push(
          <div key={groupKey} className="grouped-section">
            {/* All group data rows */}
            {value.map((row, index) => (
              <div key={index} className="data-row grouped-row">
                {/* Show grouped field values */}
                {groupedFields.map((field, fieldIndex) => {
                  const currentValue = getFieldValue(field, row);
                  let shouldShowValue = false;
                  let displayValue = '';

                  // Create a hierarchical composite key for this group value
                  // We need to determine the path up to this field level
                  const rowPath = findRowPath(row, nestedGroups);
                  let compositeKey = `${field.field_name}_${currentValue}`;

                  if (rowPath && typeof rowPath === 'string' && fieldIndex > 0) {
                    // For nested fields, use the hierarchical path
                    const pathParts = rowPath.split('|');
                    const currentPath = pathParts.slice(0, fieldIndex + 1).join('|');
                    compositeKey = `${field.field_name}_${currentPath}`;
                  }

                  console.log("compositeKey: ", compositeKey, "printedGroups[compositeKey]: ", printedGroups[compositeKey]);
                  // Show value only if not printed before
                  if (printedGroups[compositeKey] === false) {
                    shouldShowValue = true;
                    printedGroups[compositeKey] = true;

                    // Get count from calculatedGroup using composite key
                    const count = calculatedGroup[compositeKey] || 0;

                    console.log(`Group count for ${currentValue}:`, {
                      compositeKey,
                      count,
                      calculatedGroup: calculatedGroup[compositeKey]
                    });

                    displayValue = `${currentValue} (${count})`;
                  }

                  return (
                    <div
                      key={field.id}
                      className="data-cell grouped-field-cell"
                      style={{
                        textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left',
                        justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {shouldShowValue ? displayValue : '--'}
                    </div>
                  );
                })}
                {/* Data for non-grouped fields */}
                {nonGroupedFields.map(field => {
                  const tableName = field.object_name || 'main';
                  const aliasedFieldName = `${tableName}_${field.field_name}`;
                  const fieldData = row[aliasedFieldName] || row[field.field_name];
                  return (
                    <div
                      key={field.id}
                      className="data-cell"
                      style={{
                        textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left',
                        justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {formatDataCell(fieldData, field, row)}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Subtotal row for this group */}
            <div className="subtotal-row">
              {/* Empty cells for all grouped fields except the current level which shows Subtotal */}
              {groupedFields.map((field, fieldIndex) => (
                <div key={field.id} className="subtotal-cell">
                  {fieldIndex === level ? <span className="subtotal-label">Subtotal</span> : ''}
                </div>
              ))}
              {/* Show aggregated values for summarized fields, empty cells for others */}
              {nonGroupedFields.map(field => {
                const summarizedField = summarizedFields[field.id];
                if (summarizedField) {
                  const aggregatedValue = calculateReportAggregation(
                    value,
                    field,
                    summarizedField.aggregation,
                    baseCurrencyCode,
                    aggregationFormatter,
                    exchangeRateLookup
                  );
                  return (
                    <div
                      key={field.id}
                      className="subtotal-data-cell"
                      style={{
                        textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left',
                        justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {aggregatedValue}
                    </div>
                  );
                } else {
                  return (
                    <div key={field.id} className="subtotal-empty-cell"></div>
                  );
                }
              })}
            </div>
          </div>
        );
      } else {
        // Not last level → recursively render sub-groups
        const subResults = renderNestedGroups(value, level + 1, parentKey ? `${parentKey}|${key}` : key);
        console.log("subResults: ", subResults);
        results.push(...subResults);

        // Add subtotal for this group level
        const subtotalKey = `subtotal_${parentKey}_${key}_${level}`;
        results.push(
          <div key={subtotalKey} className="subtotal-row">
            {/* Empty cells for all grouped fields except the current level which shows Subtotal */}
            {groupedFields.map((field, fieldIndex) => (
              <div key={field.id} className="subtotal-cell">
                {fieldIndex === level ? <span className="subtotal-label">Subtotal</span> : ''}
              </div>
            ))}
            {/* Show aggregated values for summarized fields, empty cells for others */}
            {nonGroupedFields.map(field => {
              const summarizedField = summarizedFields[field.id];
              if (summarizedField) {
                // Calculate subtotal for this group - get all rows from nested structure
                const getAllRows = (obj) => {
                  let allRows = [];
                  for (const subKey in obj) {
                    const subValue = obj[subKey];
                    if (Array.isArray(subValue)) {
                      allRows = allRows.concat(subValue);
                    } else if (typeof subValue === 'object' && subValue !== null) {
                      allRows = allRows.concat(getAllRows(subValue));
                    }
                  }
                  return allRows;
                };

                const groupData = getAllRows(value);
                const aggregatedValue = calculateReportAggregation(
                  groupData,
                  field,
                  summarizedField.aggregation,
                  baseCurrencyCode,
                  aggregationFormatter,
                  exchangeRateLookup
                );
                return (
                  <div
                    key={field.id}
                    className="subtotal-data-cell"
                    style={{
                      textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left',
                      justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'flex-end' : 'flex-start'
                    }}
                  >
                    {aggregatedValue}
                  </div>
                );
              } else {
                return (
                  <div key={field.id} className="subtotal-empty-cell"></div>
                );
              }
            })}
          </div>
        );
      }

    }

    return results;
  }


  return (
    <>
      {renderNestedGroups(nestedGroups)}

      {/* Grand total row */}
      <div className="grand-total-row">
        {/* Empty cells for all grouped fields except the last one which shows Grand Total */}
        {groupedFields.map((field, fieldIndex) => (
          <div key={field.id} className="grand-total-cell">
            {fieldIndex === groupedFields.length - 1 ? <span className="grand-total-label">Grand Total ({grandTotal})</span> : ''}
          </div>
        ))}
        {/* Show aggregated values for summarized fields, empty cells for others */}
        {nonGroupedFields.map(field => {
          const summarizedField = summarizedFields[field.id];
          if (summarizedField) {
            const aggregatedValue = calculateReportAggregation(
              data,
              field,
              summarizedField.aggregation,
              baseCurrencyCode,
              aggregationFormatter,
              exchangeRateLookup
            );
            return (
              <div
                key={field.id}
                className="grand-total-data-cell"
                style={{
                  textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left',
                  justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'flex-end' : 'flex-start'
                }}
              >
                {aggregatedValue}
              </div>
            );
          } else {
            return (
              <div key={field.id} className="grand-total-empty-cell"></div>
            );
          }
        })}
      </div>
    </>
  );
};

const ReportPreview = ({
  reportData,
  selectedFields,
  selectedGroups,
  previewData,
  filters,
  previewLoading = false,
  grouping = null,
  onColumnAction = () => { },
  summarizedFields = {},
  summarizedData = {}, // Add summarizedData prop
  onSummarizedDataChange = () => { }, // New prop to pass summarized data back
  charts = [], // New prop for charts
  chartsVersion = 0, // Version to force re-render when charts change
  onEditChart = () => { }, // Edit chart handler
  onDeleteChart = () => { }, // Delete chart handler
  onViewChart = () => { }, // View chart handler
  reportSortKeys = [], // report.config.sort — multi-level ORDER BY
}) => {
  const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
  const [exchangeRateLookup, setExchangeRateLookup] = useState(null);
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const currencyApi = new CurrencyApi();
        const res = await currencyApi.fetchAll();
        const list = res?.data?.data || res?.data || [];
        const arr = Array.isArray(list) ? list : [];
        const baseRow = arr.find((r) => r.is_base_currency === true);
        if (!cancelled) {
          setBaseCurrencyCode(
            baseRow?.currency_code
              ? String(baseRow.currency_code).trim()
              : null
          );
          setExchangeRateLookup(buildExchangeRateLookupFromCurrencyList(arr));
        }
      } catch (e) {
        console.error('ReportPreview: failed to load base currency', e);
        if (!cancelled) {
          setExchangeRateLookup(null);
        }
      }
      try {
        const s = await getMultiCurrencySettings();
        if (!cancelled) setMultiCurrencyEnabled(!!s.multiCurrencyEnabled);
      } catch {
        if (!cancelled) setMultiCurrencyEnabled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reportConversionBaseCode = multiCurrencyEnabled ? baseCurrencyCode : null;
  const reportConversionLookup = multiCurrencyEnabled ? exchangeRateLookup : null;

  const formatPreviewDataCell = (fieldData, field, row) => {
    const money = formatReportCurrencyCell(
      fieldData,
      field,
      row,
      reportConversionBaseCode,
      resolveCurrency('INR'),
      reportConversionLookup
    );
    if (money != null) return money;
    return formatValue(
      fieldData,
      field.data_type,
      field.display_config || field.displayConfig
    );
  };

  // Debug: Log the summarized fields to see what's being passed
  console.log('ReportPreview received summarizedFields:', summarizedFields);

  // Debug: Log charts data to track updates
  console.log('ReportPreview received charts:', charts);
  console.log('ReportPreview selectedFields:', selectedFields);
  console.log('ReportPreview selectedGroups:', selectedGroups);
  console.log('ReportPreview grouping:', grouping);

  // Function to extract summarized data for charts
  // Helper function to get field ID from various possible properties
  const getFieldId = (field) => {
    return field.id || field.field_id || field.field_config_id || `${field.object_name}_${field.field_name}`;
  };

  const sqlSortInfoForField = (field) => {
    if (!reportSortKeys?.length || !field) return null;
    const id = getFieldId(field);
    const idx = reportSortKeys.findIndex(k => k.field_config_id === id);
    if (idx < 0) return null;
    return {
      level: idx + 1,
      direction: reportSortKeys[idx].direction === 'DESC' ? 'desc' : 'asc',
    };
  };

  const sqlSortLevelsCount = reportSortKeys.length;

  const extractSummarizedData = () => {
    if (!previewData || previewData.length === 0) {
      return {};
    }

    // Check if we have grouping from either source
    const hasGroupingFromAPI = grouping && grouping.hasGrouping;
    const hasGroupingFromSelected = selectedGroups && selectedGroups.length > 0;

    if (!hasGroupingFromAPI && !hasGroupingFromSelected) {
      return {};
    }

    const previewAggregationFormatter = makePreviewAggregationFormat(reportConversionBaseCode);

    const summarizedData = {};

    // Get grouped fields - prioritize selectedGroups (frontend) over API response
    let groupedFields = [];
    if (hasGroupingFromSelected) {
      // Use selectedGroups which contains the field IDs directly
      groupedFields = selectedGroups;
      console.log('Using selectedGroups for grouped fields:', groupedFields);
    } else if (hasGroupingFromAPI) {
      // Fallback to API response
      groupedFields = grouping.groupedFields || [];
      console.log('Using API grouping for grouped fields:', groupedFields);
    }

    // Get summarized fields (fields that have aggregation)
    const fieldsWithAggregation = Object.keys(summarizedFields).map(fieldId => {
      const field = selectedFields.find(f => f.id === fieldId || f.field_id === fieldId);
      if (field) {
        console.log('Found field for aggregation:', {
          fieldId,
          field: field,
          fieldId: field.id,
          field_id: field.field_id,
          field_config_id: field.field_config_id
        });
        return { ...field, aggregation: summarizedFields[fieldId].aggregation };
      }
      return null;
    }).filter(Boolean);

    // Add summarized field metadata to the structure
    if (fieldsWithAggregation.length > 0) {
      if (!summarizedData._metadata) {
        summarizedData._metadata = {
          group_fields: {},
          summarized_fields: {}
        };
      }

      fieldsWithAggregation.forEach(field => {
        summarizedData._metadata.summarized_fields[field.field_name] = {
          field_id: getFieldId(field),
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          field_type: field.field_type,
          is_required: field.is_required,
          is_unique: field.is_unique,
          default_value: field.default_value,
          field_length: field.field_length,
          decimal_places: field.decimal_places,
          picklist_values: field.picklist_values,
          lookup_object: field.lookup_object,
          lookup_field: field.lookup_field,
          help_text: field.help_text,
          description: field.description,
          aggregation: field.aggregation
        };
      });
    }

    console.log('Extracting summarized data:', {
      groupedFields: groupedFields.map(f => f.field_name),
      fieldsWithAggregation: fieldsWithAggregation.map(f => ({ name: f.field_name, aggregation: f.aggregation }))
    });

    // For each grouped field, create a map of group values to aggregated values
    groupedFields.forEach(groupField => {
      const groupFieldName = groupField.field_name;

      console.log('Processing group field:', {
        groupFieldName,
        groupField: groupField,
        fieldId: groupField.id,
        field_id: groupField.field_id,
        field_config_id: groupField.field_config_id
      });

      // Add group field metadata to the summarized data structure
      if (!summarizedData._metadata) {
        summarizedData._metadata = {
          group_fields: {},
          summarized_fields: {}
        };
      }

      // Store group field metadata
      summarizedData._metadata.group_fields[groupFieldName] = {
        field_id: getFieldId(groupField),
        field_name: groupField.field_name,
        field_label: groupField.field_label,
        data_type: groupField.data_type,
        object_name: groupField.object_name,
        field_type: groupField.field_type,
        is_required: groupField.is_required,
        is_unique: groupField.is_unique,
        default_value: groupField.default_value,
        field_length: groupField.field_length,
        decimal_places: groupField.decimal_places,
        picklist_values: groupField.picklist_values,
        lookup_object: groupField.lookup_object,
        lookup_field: groupField.lookup_field,
        help_text: groupField.help_text,
        description: groupField.description
      };
      const groupValues = new Set();

      // Collect all unique group values
      previewData.forEach(row => {
        const tableName = groupField.object_name || 'main';
        const aliasedFieldName = `${tableName}_${groupField.field_name}`;
        const fieldData = row[aliasedFieldName] || row[groupField.field_name];

        let groupValue = 'N/A';
        if (fieldData) {
          if (typeof fieldData === 'object' && fieldData !== null) {
            groupValue = fieldData.displayValue || fieldData.value || 'N/A';
          } else {
            groupValue = fieldData;
          }
        }
        groupValues.add(groupValue);
      });

      // For each group value, calculate aggregated values for each summarized field
      summarizedData[groupFieldName] = {};

      groupValues.forEach(groupValue => {
        summarizedData[groupFieldName][groupValue] = {};

        // Filter rows for this group value
        const rowsForGroup = previewData.filter(row => {
          const tableName = groupField.object_name || 'main';
          const aliasedFieldName = `${tableName}_${groupField.field_name}`;
          const fieldData = row[aliasedFieldName] || row[groupField.field_name];

          let rowGroupValue = 'N/A';
          if (fieldData) {
            if (typeof fieldData === 'object' && fieldData !== null) {
              rowGroupValue = fieldData.displayValue || fieldData.value || 'N/A';
            } else {
              rowGroupValue = fieldData;
            }
          }
          return rowGroupValue === groupValue;
        });

        // Calculate aggregated values for each summarized field
        fieldsWithAggregation.forEach(summarizedField => {
          const aggregation = summarizedField.aggregation;
          const aggregatedValue = calculateReportAggregation(
            rowsForGroup,
            summarizedField,
            aggregation,
            reportConversionBaseCode,
            previewAggregationFormatter,
            reportConversionLookup
          );
          summarizedData[groupFieldName][groupValue][summarizedField.field_name] = {
            value: aggregatedValue,
            aggregation: aggregation,
            field_label: summarizedField.field_label,
            field_id: getFieldId(summarizedField),
            field_name: summarizedField.field_name,
            data_type: summarizedField.data_type,
            object_name: summarizedField.object_name,
            field_type: summarizedField.field_type,
            is_required: summarizedField.is_required,
            is_unique: summarizedField.is_unique,
            default_value: summarizedField.default_value,
            field_length: summarizedField.field_length,
            decimal_places: summarizedField.decimal_places,
            picklist_values: summarizedField.picklist_values,
            lookup_object: summarizedField.lookup_object,
            lookup_field: summarizedField.lookup_field,
            help_text: summarizedField.help_text,
            description: summarizedField.description
          };
        });
      });
    });

    // Add additional metadata about the report and data structure
    if (!summarizedData._metadata) {
      summarizedData._metadata = {
        group_fields: {},
        summarized_fields: {}
      };
    }

    summarizedData._metadata.report_info = {
      total_records: previewData.length,
      group_count: groupedFields.length,
      summarized_field_count: fieldsWithAggregation.length,
      extraction_timestamp: new Date().toISOString(),
      data_structure_version: '2.0'
    };

    summarizedData._metadata.data_summary = {
      available_groups: Object.keys(summarizedData).filter(key => key !== '_metadata'),
      available_summarized_fields: fieldsWithAggregation.map(f => f.field_name),
      sample_data: {}
    };

    // Add sample data for each group
    Object.keys(summarizedData).forEach(groupKey => {
      if (groupKey !== '_metadata') {
        const groupData = summarizedData[groupKey];
        const sampleGroupValue = Object.keys(groupData)[0];
        if (sampleGroupValue) {
          summarizedData._metadata.data_summary.sample_data[groupKey] = {
            sample_group_value: sampleGroupValue,
            available_summarized_fields: Object.keys(groupData[sampleGroupValue])
          };
        }
      }
    });

    console.log('Extracted summarized data with enhanced metadata:', summarizedData);
    return summarizedData;
  };

  // Extract summarized data when previewData, summarizedFields, or grouping changes
  React.useEffect(() => {
    if (previewData && previewData.length > 0 &&
      (Object.keys(summarizedFields).length > 0 || (selectedGroups && selectedGroups.length > 0))) {
      const summarizedData = extractSummarizedData();
      onSummarizedDataChange(summarizedData);
    }
  }, [previewData, summarizedFields, grouping, selectedGroups, baseCurrencyCode, multiCurrencyEnabled, exchangeRateLookup]);

  // Debug: Check which fields should be summarized
  selectedFields.forEach(field => {
    const isSummarized = summarizedFields[field.id];
    console.log(`Field ${field.field_name} (ID: ${field.id}): isSummarized = ${!!isSummarized}, aggregation = ${isSummarized?.aggregation || 'none'}`);
  });

  // Helper function to check if a field is grouped
  const isFieldGrouped = (field) => {
    return selectedGroups.some(group => group.id === field.id);
  };

  // Create grouping information from selectedGroups
  const groupingInfo = {
    hasGrouping: selectedGroups.length > 0,
    groupedFields: selectedGroups.map(group => ({
      field_name: group.field_name,
      field_label: group.field_label,
      object_name: group.object_name,
      data_type: group.data_type,
      id: group.id
    }))
  };

  console.log('ReportPreview received props:', {
    selectedFields: selectedFields.length,
    selectedGroups: selectedGroups.length,
    grouping,
    previewDataLength: previewData.length
  });

  console.log('Grouping details:', {
    hasGrouping: grouping?.hasGrouping,
    groupedFields: grouping?.groupedFields,
    groupingType: typeof grouping,
    groupingKeys: grouping ? Object.keys(grouping) : 'null'
  });

  console.log('Created grouping info:', groupingInfo);

  const getResolvedField = (field) => {
    const fieldId = getFieldId(field);

    const matchFromSelected = selectedFields.find(sel => getFieldId(sel) === fieldId);
    if (matchFromSelected) {
      return matchFromSelected;
    }

    const matchFromGroups = selectedGroups.find(group => getFieldId(group) === fieldId);
    if (matchFromGroups) {
      return matchFromGroups;
    }

    return {
      ...field,
      id: fieldId
    };
  };

  const getFieldSummarization = (field) => {
    const fieldId = getFieldId(field);
    return summarizedFields[fieldId];
  };

  return (
    <div className="preview-panel">
      <div className="preview-banner">
        <span className="preview-message">
          Previewing a limited number of records. Run the report to see everything.
        </span>
      </div>

      {previewLoading ? (
        <div className="preview-loading">
          <div className="spinner"></div>
          <p>Loading preview data...</p>
        </div>
      ) : selectedFields.length > 0 ? (
        <div className="report-preview">
          {/* Charts Section */}
          {charts && charts.length > 0 && (
            <div className="charts-preview-section">
              <div className="charts-preview-header">
                <h4>Charts</h4>
              </div>
              <div className={`charts-preview-grid ${charts.length === 1 ? 'single-chart' : ''}`} key={`charts-grid-${chartsVersion}-${charts.length}-${charts.map(c => `${c.id}-${c.sortOrder}-${c.columnSpan}`).join('-')}`}>
                {charts
                  .sort((a, b) => (a.sortOrder || 1) - (b.sortOrder || 1)) // Sort charts by sortOrder
                  .map((chart) => {
                    // Use columnSpan from chart configuration
                    const getChartLayoutClass = (columnSpan) => {
                      if (columnSpan === 2) return 'chart-full-width';
                      return 'chart-half';
                    };

                    const layoutClass = getChartLayoutClass(chart.columnSpan || 1);

                    return (
                      <div
                        key={`${chart.id}-${chart.sortOrder}-${chart.columnSpan}-${chartsVersion}`}
                        className={`chart-wrapper ${layoutClass}`}
                      >
                        <ChartDisplay
                          key={`chart-display-${chart.id}-${chart.sortOrder}-${chart.columnSpan}-${chartsVersion}`}
                          chart={chart}
                          selectedFields={selectedFields}
                          summarizedFields={summarizedFields}
                          selectedGroups={selectedGroups}
                          summarizedData={summarizedData}
                          previewData={previewData}
                          onEdit={onEditChart}
                          onDelete={onDeleteChart}
                          onView={onViewChart}
                          isPreview={false}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          )}


          <div className="report-headers">
            {groupingInfo && groupingInfo.hasGrouping ? (
              <>
                {groupingInfo.groupedFields.map(field => {
                  const resolvedField = getResolvedField(field);
                  const fieldKey = getFieldId(resolvedField);
                  const summaryInfo = getFieldSummarization(resolvedField);

                  return (
                    <div key={fieldKey} className="header-cell grouped-header">
                      <ColumnHeaderDropdown
                        field={resolvedField}
                        onSort={onColumnAction}
                        onGroup={onColumnAction}
                        onSummarize={onColumnAction}
                        onMove={onColumnAction}
                        onRemove={onColumnAction}
                        isSummarized={!!summaryInfo}
                        aggregationType={summaryInfo?.aggregation}
                        isGrouped={true}
                        sqlSortInfo={sqlSortInfoForField(resolvedField)}
                        sqlSortLevelsCount={sqlSortLevelsCount}
                      />
                    </div>
                  );
                })}
                {selectedFields.filter(field =>
                  !groupingInfo.groupedFields.some(groupField =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                  )
                ).map(field => {
                  const resolvedField = getResolvedField(field);
                  const fieldKey = getFieldId(resolvedField);
                  const summaryInfo = getFieldSummarization(resolvedField);

                  return (
                    <div key={fieldKey} className="header-cell">
                      <ColumnHeaderDropdown
                        field={resolvedField}
                        onSort={onColumnAction}
                        onGroup={onColumnAction}
                        onSummarize={onColumnAction}
                        onMove={onColumnAction}
                        onRemove={onColumnAction}
                        isSummarized={!!summaryInfo}
                        aggregationType={summaryInfo?.aggregation}
                        isGrouped={isFieldGrouped(resolvedField)}
                        sqlSortInfo={sqlSortInfoForField(resolvedField)}
                        sqlSortLevelsCount={sqlSortLevelsCount}
                      />
                    </div>
                  );
                })}
              </>
            ) : (
              selectedFields.map(field => {
                const resolvedField = getResolvedField(field);
                const fieldKey = getFieldId(resolvedField);
                const summaryInfo = getFieldSummarization(resolvedField);

                return (
                  <div key={fieldKey} className="header-cell">
                    <ColumnHeaderDropdown
                      field={resolvedField}
                      onSort={onColumnAction}
                      onGroup={onColumnAction}
                      onSummarize={onColumnAction}
                      onMove={onColumnAction}
                      onRemove={onColumnAction}
                      isSummarized={!!summaryInfo}
                      aggregationType={summaryInfo?.aggregation}
                      isGrouped={isFieldGrouped(resolvedField)}
                      sqlSortInfo={sqlSortInfoForField(resolvedField)}
                      sqlSortLevelsCount={sqlSortLevelsCount}
                    />
                  </div>
                );
              })
            )}
          </div>

          {previewData.length > 0 ? (
            <div className="report-data">
              {groupingInfo && groupingInfo.hasGrouping ? (
                // Render grouped data
                renderGroupedData(
                  previewData,
                  selectedFields,
                  groupingInfo,
                  summarizedFields,
                  reportConversionBaseCode,
                  reportConversionLookup,
                  reportSortKeys
                )
              ) : (
                // Render regular data
                previewData.map((row, index) => (
                  <div key={index} className="data-row regular-row">
                    {selectedFields.map(field => {
                      // Use the aliased field name from the query
                      const tableName = field.object_name || 'main';
                      const aliasedFieldName = `${tableName}_${field.field_name}`;
                      const fieldData = row[aliasedFieldName] || row[field.field_name];
                      return (
                        <div
                          key={field.id}
                          className="data-cell"
                          style={{
                            textAlign: ['number', 'integer', 'decimal', 'currency', 'percent', 'rollupsummary', 'autonumber', 'numeric', 'float', 'double', 'real', 'bigint', 'smallint', 'money']
                              .includes(field.data_type?.toLowerCase()) ? 'right' : 'left',
                            justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent', 'rollupsummary', 'autonumber', 'numeric', 'float', 'double', 'real', 'bigint', 'smallint', 'money']
                              .includes(field.data_type?.toLowerCase()) ? 'flex-end' : 'flex-start'
                          }}
                        >
                          {formatPreviewDataCell(fieldData, field, row)}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="no-data-message">
              <p>No records returned in preview. Try running the report or editing report filters.</p>
              <div className="suggestions">
                <p>• Show <a href="#">All accounts</a>.</p>
                <p>• Set the Created Date filter to <a href="#">All Time</a>.</p>
                <p>• <a href="#">Edit other filters</a> in the filter panel.</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-preview">
          <h3>Add columns to see your report</h3>
          <p>Select fields from the left panel to build your report.</p>
        </div>
      )}
    </div>
  );
};

export default ReportPreview;
